// ABC tune importer (abcnotation.com, standard 2.1) → the Score model.
//
// ABC is the plain-text format RealSheet, abcjs, EasyABC and most folk and
// lead-sheet tunebooks use. This reads ONE tune — `splitAbcBook` (book.ts)
// splits a file into self-contained one-tune chunks — straight into the
// Score model, the way the openbook importer reads its LilyPond subset:
// spelled pitches (key signature + bar accidentals, ties carry an
// accidental over the barline), notated durations (L:, lengths, broken
// rhythm, explicit tuplets, tied splits for lengths no single value holds),
// chords on one stem, grace notes, slurs, decorations, chord symbols as the
// harmony lane, annotations as directions or note text, w: lyrics (verses,
// hyphens, holds, bar skips), repeats and endings, pickups, meter and key
// changes, overlays (&) and V: voices as parts.
//
// The importer never throws on odd music: what it cannot represent is
// reported in `warnings` and the rest of the tune still loads.

import { chordFromType } from '../chart';
import type { BarlineClose, BarlineOpen } from '../ireal/types';
import {
  addF,
  compareF,
  durationQuarters,
  equalsF,
  frac,
  fractionToNumber,
  measureQuarters,
  mulF,
  NOTE_VALUES,
  type Duration,
  type Fraction,
} from '../score/duration';
import { midiOf, transposePitch, type SpelledPitch, type Step } from '../score/pitch';
import { normalizeScore } from '../score/normalize';
import type {
  Articulation,
  Clef,
  DynamicMark,
  FormMeasure,
  HarmonySymbol,
  KeySignature,
  LyricSyllable,
  NoteEvent,
  NoteHead,
  Ornament,
  Part,
  PartMeasure,
  PartRole,
  Score,
  Spanner,
  TupletGroup,
  Voice,
  VoiceItem,
} from '../score/types';
import { uid } from '../song';
import { splitAbcBook } from './book';
import { parseAbcChordSymbol, type AbcChordSymbol } from './chords';
import { decodeAbcText, normalizeAbcTitle, stripComment } from './text';

export interface AbcImport {
  title: string;
  composer?: string;
  score: Score;
  /** Things the importer could not represent, in plain words. */
  warnings: string[];
}

// ---------------------------------------------------------------- vocabulary

const SHARP_ORDER: Step[] = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];
const FLAT_ORDER: Step[] = ['B', 'E', 'A', 'D', 'G', 'C', 'F'];
const LETTER_FIFTHS: Record<Step, number> = { F: -1, C: 0, G: 1, D: 2, A: 3, E: 4, B: 5 };
const MAJOR_TONIC: Record<number, string> = {
  [-7]: 'Cb',
  [-6]: 'Gb',
  [-5]: 'Db',
  [-4]: 'Ab',
  [-3]: 'Eb',
  [-2]: 'Bb',
  [-1]: 'F',
  0: 'C',
  1: 'G',
  2: 'D',
  3: 'A',
  4: 'E',
  5: 'B',
  6: 'F#',
  7: 'C#',
};
const MINOR_TONIC: Record<number, string> = {
  [-7]: 'Ab',
  [-6]: 'Eb',
  [-5]: 'Bb',
  [-4]: 'F',
  [-3]: 'C',
  [-2]: 'G',
  [-1]: 'D',
  0: 'A',
  1: 'E',
  2: 'B',
  3: 'F#',
  4: 'C#',
  5: 'G#',
  6: 'D#',
  7: 'A#',
};

/** Fifths relative to the major key on the same tonic. */
const MODES: { re: RegExp; offset: number; minor: boolean }[] = [
  { re: /^(?:maj|ion)/i, offset: 0, minor: false },
  { re: /^(?:min|aeo)/i, offset: -3, minor: true },
  { re: /^mix/i, offset: -1, minor: false },
  { re: /^dor/i, offset: -2, minor: true },
  { re: /^phr/i, offset: -4, minor: true },
  { re: /^lyd/i, offset: 1, minor: false },
  { re: /^loc/i, offset: -5, minor: true },
  { re: /^m$/i, offset: -3, minor: true },
];

/** "To Coda" is the jump; the other directives are what the flattener reads. */
const TO_CODA = /^to\s+coda\b/i;
const DIRECTIVE = /^(?:D\.\s*[CS]\.|Da\s+Capo|Dal\s+Segno|Fine\b)/i;

const DYNAMICS = new Set([
  'pppp',
  'ppp',
  'pp',
  'p',
  'mp',
  'mf',
  'f',
  'ff',
  'fff',
  'ffff',
  'sfz',
  'sf',
  'fp',
]);

/** ABC 2.1's default decoration shortcuts (others need a U: definition). */
const SHORTCUTS = new Set(['H', 'L', 'M', 'O', 'P', 'S', 'T', 'u', 'v']);

const SECTION_LABEL =
  /^(?:[A-H](?:\d|'|′)?|intro|verse|chorus|bridge|coda|outro|tag|interlude|vamp|solos?|ending|head)$/i;

/** Straight written values, longest first (dots 0–2, like the chart lift). */
const STRAIGHT: { duration: Duration; quarters: Fraction }[] = NOTE_VALUES.flatMap((base) =>
  ([0, 1, 2] as const).map((dots) => {
    const duration: Duration = dots ? { base, dots } : { base };
    return { duration, quarters: durationQuarters(duration) };
  }),
).sort((a, b) => compareF(b.quarters, a.quarters));

const ZERO = frac(0);
const isPositive = (f: Fraction) => f.num > 0;
const sub = (a: Fraction, b: Fraction) => addF(a, mulF(b, frac(-1)));
const maxF = (a: Fraction, b: Fraction) => (compareF(a, b) >= 0 ? a : b);

// --------------------------------------------------------------- tune state

type Meter = [number, number] | null;

interface Head {
  /** Concert pitch (after octave= and transpose=). */
  pitch: SpelledPitch;
  /** As written in the ABC, which is what ties and bar accidentals follow. */
  written: { step: Step; octave: number; alter: number };
  tie: boolean;
}

interface RawEvent {
  id: string;
  rest: boolean;
  heads: Head[];
  /** Written length in quarters, before tuplet scaling. */
  written: Fraction;
  tuplet?: { id: string; p: number; q: number };
  grace?: boolean;
  /** Sounding onset within the bar, in quarters. */
  at: Fraction;
  articulations?: Articulation[];
  ornaments?: Ornament[];
  dynamic?: DynamicMark;
  words?: string;
  wordsBelow?: string;
  lyrics?: (LyricSyllable | undefined)[];
}

interface Lane {
  events: RawEvent[];
  pos: Fraction;
}

interface Bar {
  lanes: Lane[];
  lane: number;
  chords: { at: Fraction; texts: string[] }[];
  meter: Meter;
  open?: BarlineOpen;
  close?: BarlineClose;
  ending?: number;
  section?: string;
  segno?: boolean;
  coda?: boolean;
  fermata?: boolean;
  directive?: string;
  staffText?: string;
  keySig?: KeySignature;
  /** Tempo change in quarter notes per minute. */
  tempoQ?: number;
}

interface KeyState {
  accidentals: Map<Step, number>;
  signature: KeySignature;
}

type StreamItem = { kind: 'note' | 'rest'; event: RawEvent } | { kind: 'bar' };

interface VoiceState {
  id: string;
  name?: string;
  clef: Clef;
  octave: number;
  transpose: number;
  bars: Bar[];
  bar: Bar;
  /** Flags waiting for the next bar (a field or sign that arrived mid-bar). */
  next: Partial<Pick<Bar, 'section' | 'segno' | 'coda' | 'keySig' | 'tempoQ' | 'staffText'>>;
  unit: Fraction;
  meter: Meter;
  key: KeyState;
  barAccidentals: Map<string, number>;
  /** Alterations carried into the next note by a tie (step+octave → alter). */
  tieCarry: Map<string, number>;
  pendingChords: string[];
  pendingAnnotations: string[];
  pendingDecorations: string[];
  pendingSlurs: number;
  slurStack: string[];
  hairpin: { type: 'cresc' | 'dim'; from: string } | null;
  tuplet: { id: string; p: number; q: number; left: number } | null;
  brokenNext: Fraction | null;
  last: RawEvent | null;
  stream: StreamItem[];
  unalignedFrom: number;
  verse: number;
  afterLyrics: boolean;
  hasNotes: boolean;
}

interface KeyField {
  key?: KeyState;
  clef?: Clef;
  octave?: number;
  transpose?: number;
}

// ------------------------------------------------------------------ fields

const FIELD = /^([A-Za-z+]):(.*)$/;

function fieldOf(line: string, inBody: boolean): { letter: string; value: string } | null {
  const m = FIELD.exec(line);
  if (!m) return null;
  // In the body, "B:|" is the note B before a repeat, not a field.
  if (inBody && /^[A-Ga-g]$/.test(m[1]) && /^\s*[|:\]]/.test(m[2])) return null;
  return { letter: m[1], value: m[2].trim() };
}

function parseMeter(value: string): Meter | undefined {
  const v = value.trim();
  if (!v || /^none$/i.test(v)) return null;
  if (v === 'C') return [4, 4];
  if (v === 'C|') return [2, 2];
  const m = /^\(?([\d+\s]+)\)?\s*\/\s*(\d+)/.exec(v);
  if (!m) return undefined;
  const num = m[1]
    .split('+')
    .map((n) => Number(n.trim()))
    .reduce((s, n) => s + (Number.isFinite(n) ? n : 0), 0);
  const den = Number(m[2]);
  if (num <= 0 || ![1, 2, 4, 8, 16, 32].includes(den)) return undefined;
  return [num, den];
}

/** L: as a length in quarter notes. */
function parseUnit(value: string): Fraction | undefined {
  const m = /^\s*(\d+)\s*(?:\/\s*(\d+))?/.exec(value);
  if (!m) return undefined;
  const num = Number(m[1]);
  const den = m[2] ? Number(m[2]) : 1;
  if (num <= 0 || den <= 0) return undefined;
  return frac(num * 4, den);
}

/** Q: as quarter notes per minute, its words, or a bare legacy number. */
function parseTempo(value: string): { quarterBpm?: number; bare?: number; text?: string } {
  const texts = [...value.matchAll(/"([^"]*)"/g)]
    .map((m) => decodeAbcText(m[1]).trim())
    .filter(Boolean);
  const rest = value.replace(/"[^"]*"/g, ' ');
  const out: { quarterBpm?: number; bare?: number; text?: string } = {};
  if (texts.length) out.text = texts.join(' ');
  const beat = /((?:\d+\s*\/\s*\d+\s*)+)=\s*(\d+(?:\.\d+)?)/.exec(rest);
  if (beat) {
    let quarters = ZERO;
    for (const f of beat[1].matchAll(/(\d+)\s*\/\s*(\d+)/g)) {
      quarters = addF(quarters, frac(Number(f[1]) * 4, Number(f[2])));
    }
    const bpm = Number(beat[2]);
    if (isPositive(quarters) && bpm > 0) {
      out.quarterBpm = Math.round(bpm * fractionToNumber(quarters));
    }
    return out;
  }
  const bare = /^\s*(\d+(?:\.\d+)?)\s*$/.exec(rest);
  if (bare) out.bare = Number(bare[1]);
  return out;
}

/**
 * A clef name. The one-letter forms (G, F, C and their line numbers) only
 * count after `clef=`, since a bare "G" or "F" in K: is the key.
 */
function clefOf(word: string, afterClefEquals = false): Clef | undefined {
  const w = word.toLowerCase();
  if (/^treble[-_]?8$/.test(w) || w === 'treble8vb' || (afterClefEquals && /^g[-_]?8$/.test(w))) {
    return 'treble8vb';
  }
  if (w === 'treble' || w === 'none' || (afterClefEquals && /^g\d?$/.test(w))) return 'treble';
  if (/^bass\d?$/.test(w) || (afterClefEquals && /^f\d?$/.test(w))) return 'bass';
  if (w === 'alto' || (afterClefEquals && (w === 'c' || w === 'c3'))) return 'alto';
  if (w === 'tenor' || (afterClefEquals && w === 'c4')) return 'tenor';
  if (w === 'perc' || w === 'percussion' || (afterClefEquals && w === 'p')) return 'treble';
  return undefined;
}

function keyStateOf(fifths: number, minor: boolean, writtenTonic?: string): KeyState {
  const accidentals = new Map<Step, number>();
  const order = fifths >= 0 ? SHARP_ORDER : FLAT_ORDER;
  for (let k = 0; k < Math.abs(fifths); k++) {
    const step = order[k % 7];
    accidentals.set(step, (accidentals.get(step) ?? 0) + (fifths >= 0 ? 1 : -1));
  }
  // The engraver reads −7…7; a wider key prints as its enharmonic twin while
  // every note keeps its true spelling.
  const printable = fifths > 7 ? fifths - 12 : fifths < -7 ? fifths + 12 : fifths;
  const tonic = (minor ? MINOR_TONIC : MAJOR_TONIC)[printable];
  return {
    accidentals,
    signature: {
      tonic: writtenTonic && printable === fifths ? writtenTonic : tonic,
      mode: minor ? 'minor' : 'major',
    },
  };
}

function parseKeyField(value: string, warn: (message: string) => void): KeyField {
  const tokens = value.trim().split(/\s+/).filter(Boolean);
  const out: KeyField = {};
  let i = 0;
  const first = tokens[0];
  if (first && /^none$/i.test(first)) {
    out.key = keyStateOf(0, false);
    i = 1;
  } else if (first && /^H[Pp]$/.test(first)) {
    out.key = keyStateOf(2, false);
    i = 1;
  } else if (first && /^[A-G][#b]?/.test(first) && !clefOf(first)) {
    const m = /^([A-G])([#b]?)(.*)$/.exec(first)!;
    const step = m[1] as Step;
    const alter = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
    let modeWord = m[3];
    i = 1;
    if (!modeWord && tokens[1] && MODES.some((mode) => mode.re.test(tokens[1]))) {
      modeWord = tokens[1];
      i = 2;
    }
    const mode = modeWord ? MODES.find((candidate) => candidate.re.test(modeWord)) : MODES[0];
    if (modeWord && !mode) warn(`Key mode "${modeWord}" read as major`);
    const chosen = mode ?? MODES[0];
    const fifths = LETTER_FIFTHS[step] + 7 * alter + chosen.offset;
    const plain = chosen.offset === 0 || (chosen.minor && chosen.offset === -3);
    out.key = keyStateOf(fifths, chosen.minor, plain ? `${step}${m[2]}` : undefined);
  }
  let explicit = false;
  const extra: [Step, number][] = [];
  for (; i < tokens.length; i++) {
    const token = tokens[i];
    const assign = /^([A-Za-z]+)=(.*)$/.exec(token);
    if (assign) {
      const name = assign[1].toLowerCase();
      if (name === 'clef') {
        const clef = clefOf(assign[2], true);
        if (clef) out.clef = clef;
      } else if (name === 'octave') {
        const n = Number(assign[2]);
        if (Number.isInteger(n)) out.octave = n;
      } else if (name === 'transpose') {
        const n = Number(assign[2]);
        if (Number.isInteger(n)) out.transpose = n;
      }
      continue;
    }
    if (token.toLowerCase() === 'exp') {
      explicit = true;
      continue;
    }
    const acc = /^(\^\^|\^|__|_|=)([A-Ga-g])$/.exec(token);
    if (acc) {
      const alter = { '^^': 2, '^': 1, __: -2, _: -1, '=': 0 }[acc[1]] ?? 0;
      extra.push([acc[2].toUpperCase() as Step, alter]);
      continue;
    }
    const clef = clefOf(token) ?? clefOf(token.replace(/[+-]8$/, ''));
    if (clef) out.clef = clef;
  }
  if (explicit || extra.length) {
    const base = explicit ? new Map<Step, number>() : new Map(out.key?.accidentals ?? []);
    for (const [step, alter] of extra) {
      if (alter === 0) base.delete(step);
      else base.set(step, alter);
    }
    out.key = {
      accidentals: base,
      signature: out.key?.signature ?? { tonic: 'C', mode: 'major' },
    };
    warn('A key signature with added accidentals is written as accidentals on the notes');
  }
  return out;
}

// ------------------------------------------------------------------ lyrics

type LyricToken =
  | { kind: 'syllable'; text: string; hyphen: boolean }
  | { kind: 'skip' }
  | { kind: 'bar' };

/** Split one w: line into syllables and the placeholders ABC aligns by. */
function lyricTokens(line: string): LyricToken[] {
  const out: LyricToken[] = [];
  let current = '';
  const flush = (hyphen: boolean) => {
    if (current === '') return false;
    out.push({ kind: 'syllable', text: decodeAbcText(current), hyphen });
    current = '';
    return true;
  };
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '\\' && i + 1 < line.length && '-_*~|'.includes(line[i + 1])) {
      current += line[++i];
      continue;
    }
    if (c === '\\' && i + 1 < line.length) {
      // a text escape (\'e, \u00e9): decodeAbcText reads it at the flush
      current += c + line[++i];
      continue;
    }
    if (/\s/.test(c)) {
      flush(false);
    } else if (c === '-') {
      // A hyphen after a space or another hyphen is a syllable of its own:
      // the word carries on under one more note.
      if (!flush(true)) {
        for (let k = out.length - 1; k >= 0; k--) {
          const token = out[k];
          if (token.kind === 'syllable') {
            token.hyphen = true;
            break;
          }
          if (token.kind === 'bar') break;
        }
        out.push({ kind: 'skip' });
      }
    } else if (c === '_' || c === '*') {
      flush(false);
      out.push({ kind: 'skip' });
    } else if (c === '|') {
      flush(false);
      out.push({ kind: 'bar' });
    } else if (c === '~') {
      current += ' ';
    } else {
      current += c;
    }
  }
  flush(false);
  return out;
}

function alignLyrics(v: VoiceState, tokens: LyricToken[], verse: number): void {
  const stream = v.stream;
  let p = v.unalignedFrom;
  for (const token of tokens) {
    if (token.kind === 'bar') {
      while (p < stream.length && stream[p].kind !== 'bar') p++;
      if (p < stream.length) p++;
      continue;
    }
    while (p < stream.length && stream[p].kind !== 'note') p++;
    if (p >= stream.length) return;
    const item = stream[p++];
    if (token.kind !== 'syllable' || item.kind !== 'note') continue;
    const text = token.text.trim();
    if (!text) continue;
    const lyrics = item.event.lyrics ?? [];
    while (lyrics.length < verse) lyrics.push(undefined);
    lyrics[verse] = { text, ...(token.hyphen ? { hyphen: true } : {}) };
    item.event.lyrics = lyrics;
  }
}

// ----------------------------------------------------------------- parser

class TuneParser {
  readonly warnings = new Set<string>();
  readonly spanners: Spanner[] = [];
  private readonly voices = new Map<string, VoiceState>();
  private readonly order: string[] = [];
  private voice!: VoiceState;
  private defaults!: {
    unit: Fraction;
    meter: Meter;
    key: KeyState;
    clef: Clef;
    octave: number;
    transpose: number;
  };
  private propagate: 'octave' | 'pitch' | 'not' = 'octave';
  private readonly decorationAliases = new Map<string, string>();
  private readonly ignoredDecorations = new Set<string>();

  title = '';
  composers: string[] = [];
  /** No music yet: a T:, C: or R: after K: still describes the tune. */
  private musicStarted = false;
  style?: string;
  tempoText?: string;
  tempoQ?: number;
  tempoBare?: number;
  partOrder?: string;
  private voiceDefs = new Map<string, string>();

  warn(message: string): void {
    this.warnings.add(message);
  }

  parse(text: string): void {
    const raw = text
      .replace(/^\uFEFF/, '')
      .replace(/\r\n?/g, '\n')
      .split('\n');
    const lines = this.joinContinuations(raw);

    let header = true;
    let headerMeter: Meter | undefined;
    let headerUnit: Fraction | undefined;
    let headerKey: KeyField = {};
    let skipping = false;

    const startBody = () => {
      header = false;
      const meter = headerMeter === undefined ? null : headerMeter;
      const unit = headerUnit ?? (meter && meter[0] / meter[1] < 0.75 ? frac(1, 4) : frac(1, 2));
      this.defaults = {
        unit,
        meter,
        key: headerKey.key ?? keyStateOf(0, false),
        clef: headerKey.clef ?? 'treble',
        octave: headerKey.octave ?? 0,
        transpose: headerKey.transpose ?? 0,
      };
      this.voice = this.voiceFor(this.voiceDefs.keys().next().value ?? '1');
    };

    for (const rawLine of lines) {
      const trimmed = rawLine.trim();
      if (skipping) {
        if (/^%%end/i.test(trimmed)) skipping = false;
        continue;
      }
      if (/^%%begin(?:text|ps|svg|js|ml)/i.test(trimmed)) {
        skipping = true;
        continue;
      }
      if (trimmed.startsWith('%%')) {
        const m = /^%%propagate-accidentals\s+(not|octave|pitch)/i.exec(trimmed);
        if (m) this.propagate = m[1].toLowerCase() as 'not' | 'octave' | 'pitch';
        continue;
      }
      const line = stripComment(rawLine).replace(/\s+$/, '');
      if (!line.trim()) continue;
      const field = fieldOf(line.trimStart(), !header);
      if (header) {
        if (!field) {
          // Music before any K: — read the tune as if K:C had been written.
          this.warn('No K: field; read in C');
          startBody();
          this.musicLine(line);
          continue;
        }
        const value = field.value;
        switch (field.letter) {
          case 'T': {
            const t = decodeAbcText(value).trim();
            if (!t) break;
            if (!this.title) this.title = normalizeAbcTitle(t);
            break;
          }
          case 'C': {
            const c = decodeAbcText(value).trim();
            if (c) this.composers.push(c);
            break;
          }
          case 'R':
            if (value && !this.style) this.style = decodeAbcText(value).trim();
            break;
          case 'M': {
            const meter = parseMeter(value);
            if (meter === undefined) this.warn(`Meter "${value}" not understood`);
            else headerMeter = meter;
            break;
          }
          case 'L': {
            const unit = parseUnit(value);
            if (unit) headerUnit = unit;
            break;
          }
          case 'Q':
            this.readHeaderTempo(value);
            break;
          case 'P':
            this.partOrder = value;
            break;
          case 'U':
            this.defineDecoration(value);
            break;
          case 'V': {
            const id = value.split(/\s+/)[0];
            if (id && !this.voiceDefs.has(id)) this.voiceDefs.set(id, value);
            break;
          }
          case 'K':
            headerKey = parseKeyField(value, (m) => this.warn(m));
            startBody();
            break;
          default:
            break;
        }
        continue;
      }

      if (field) {
        this.bodyField(field.letter, field.value);
        continue;
      }
      this.musicLine(line);
    }
    if (header) startBody();
    for (const id of this.order) this.finishVoice(this.voices.get(id)!);
  }

  /** Join music lines continued with a trailing backslash, and +: fields. */
  private joinContinuations(raw: string[]): string[] {
    const out: string[] = [];
    for (const line of raw) {
      const prev = out.length > 0 ? out[out.length - 1] : undefined;
      if (prev !== undefined && /^\+:/.test(line)) {
        out[out.length - 1] = `${prev} ${line.slice(2).trim()}`;
        continue;
      }
      if (prev !== undefined && /\\\s*$/.test(stripComment(prev)) && !/^\s*%/.test(line)) {
        const body = stripComment(prev).replace(/\\\s*$/, '');
        const nextField = /^([A-Za-z]):/.exec(line);
        const prevField = /^([A-Za-z]):/.exec(prev);
        if (prevField && nextField && prevField[1] === nextField[1]) {
          out[out.length - 1] = `${body} ${line.slice(2)}`;
          continue;
        }
        if (!prevField && !nextField) {
          out[out.length - 1] = `${body}${line}`;
          continue;
        }
      }
      out.push(line);
    }
    return out;
  }

  private readHeaderTempo(value: string): void {
    const tempo = parseTempo(value);
    if (tempo.text && !this.tempoText) this.tempoText = tempo.text;
    if (tempo.quarterBpm && this.tempoQ === undefined) this.tempoQ = tempo.quarterBpm;
    if (tempo.bare && this.tempoBare === undefined) this.tempoBare = tempo.bare;
  }

  private defineDecoration(value: string): void {
    const m = /^\s*([~H-Wh-w])\s*=\s*[!+]([^!+]+)[!+]/.exec(value);
    if (m) this.decorationAliases.set(m[1], m[2].trim());
  }

  // ------------------------------------------------------------- voices

  private voiceFor(id: string): VoiceState {
    const existing = this.voices.get(id);
    if (existing) return existing;
    const def = this.voiceDefs.get(id) ?? id;
    const params = parseKeyField(def.slice(id.length), (m) => this.warn(m));
    const name =
      /\b(?:name|nm)\s*=\s*"([^"]*)"/i.exec(def)?.[1] ??
      /\b(?:name|nm)\s*=\s*(\S+)/i.exec(def)?.[1];
    const v: VoiceState = {
      id,
      ...(name ? { name: decodeAbcText(name) } : {}),
      clef: params.clef ?? this.defaults.clef,
      octave: params.octave ?? this.defaults.octave,
      transpose: params.transpose ?? this.defaults.transpose,
      bars: [],
      bar: undefined as unknown as Bar,
      next: {},
      unit: this.defaults.unit,
      meter: this.defaults.meter,
      key: this.defaults.key,
      barAccidentals: new Map(),
      tieCarry: new Map(),
      pendingChords: [],
      pendingAnnotations: [],
      pendingDecorations: [],
      pendingSlurs: 0,
      slurStack: [],
      hairpin: null,
      tuplet: null,
      brokenNext: null,
      last: null,
      stream: [],
      unalignedFrom: 0,
      verse: 0,
      afterLyrics: false,
      hasNotes: false,
    };
    v.bar = this.newBar(v);
    this.voices.set(id, v);
    this.order.push(id);
    return v;
  }

  private newBar(v: VoiceState): Bar {
    const bar: Bar = {
      lanes: [{ events: [], pos: ZERO }],
      lane: 0,
      chords: [],
      meter: v.meter,
      ...v.next,
    };
    v.next = {};
    return bar;
  }

  private barIsEmpty(bar: Bar): boolean {
    return bar.lanes.every((lane) => lane.events.length === 0);
  }

  private bodyField(letter: string, value: string): void {
    const v = this.voice;
    if (!this.musicStarted) {
      const text = decodeAbcText(value).trim();
      if (letter === 'C') {
        if (text) this.composers.push(text);
        return;
      }
      if (letter === 'R') {
        if (text && !this.style) this.style = text;
        return;
      }
      if (letter === 'T' && !this.title) {
        if (text) this.title = normalizeAbcTitle(text);
        return;
      }
    }
    switch (letter) {
      case 'w': {
        alignLyrics(v, lyricTokens(value), v.verse);
        v.verse++;
        v.afterLyrics = true;
        break;
      }
      case 'V':
        this.switchVoice(value);
        break;
      default:
        this.inlineField(letter, value);
        break;
    }
  }

  private switchVoice(value: string): void {
    const id = value.split(/\s+/)[0];
    if (!id) return;
    if (!this.voiceDefs.has(id)) this.voiceDefs.set(id, value);
    this.voice = this.voiceFor(id);
  }

  /** K: M: L: Q: P: T: in the body or [X:…] inside a music line. */
  private inlineField(letter: string, value: string): void {
    const v = this.voice;
    const atStart = this.barIsEmpty(v.bar);
    switch (letter) {
      case 'K': {
        const field = parseKeyField(value, (m) => this.warn(m));
        if (field.clef && field.clef !== v.clef) {
          if (v.hasNotes) this.warn('A clef change mid-tune is not shown');
          else v.clef = field.clef;
        }
        if (field.octave !== undefined) v.octave = field.octave;
        if (field.transpose !== undefined) v.transpose = field.transpose;
        if (field.key) {
          v.key = field.key;
          if (atStart) v.bar.keySig = field.key.signature;
          else v.next.keySig = field.key.signature;
        }
        break;
      }
      case 'M': {
        const meter = parseMeter(value);
        if (meter === undefined) {
          this.warn(`Meter "${value}" not understood`);
          break;
        }
        v.meter = meter;
        if (atStart) v.bar.meter = meter;
        break;
      }
      case 'L': {
        const unit = parseUnit(value);
        if (unit) v.unit = unit;
        break;
      }
      case 'Q': {
        const tempo = parseTempo(value);
        const quarterBpm =
          tempo.quarterBpm ??
          (tempo.bare && v.meter ? Math.round(tempo.bare * (4 / v.meter[1])) : tempo.bare);
        if (quarterBpm) {
          if (atStart) v.bar.tempoQ = quarterBpm;
          else v.next.tempoQ = quarterBpm;
        }
        break;
      }
      case 'P': {
        const label = decodeAbcText(value).trim();
        if (!label) break;
        if (atStart) v.bar.section = label;
        else v.next.section = label;
        break;
      }
      case 'T': {
        const text = decodeAbcText(value).trim();
        if (!text) break;
        if (atStart) v.bar.staffText = text;
        else v.next.staffText = text;
        break;
      }
      case 'U':
        this.defineDecoration(value);
        break;
      case 'V':
        this.switchVoice(value);
        break;
      default:
        break;
    }
  }

  // ------------------------------------------------------------- music

  private musicLine(line: string): void {
    this.musicStarted = true;
    this.beginMusic(this.voice);
    let i = 0;
    const s = line;
    while (i < s.length) {
      const c = s[i];
      const v = this.voice;
      if (c === ' ' || c === '\t' || c === '`' || c === '$') {
        i++;
        continue;
      }
      if (c === '"') {
        const end = s.indexOf('"', i + 1);
        const text = end < 0 ? s.slice(i + 1) : s.slice(i + 1, end);
        i = end < 0 ? s.length : end + 1;
        this.quoted(v, text);
        continue;
      }
      if (c === '!') {
        const end = s.indexOf('!', i + 1);
        if (end < 0) {
          i++;
          continue;
        }
        v.pendingDecorations.push(s.slice(i + 1, end));
        i = end + 1;
        continue;
      }
      if (c === '+') {
        const end = s.indexOf('+', i + 1);
        const inner = end < 0 ? '' : s.slice(i + 1, end);
        const isNotes = /^[\^_=A-Ga-g,'\d/]+$/.test(inner);
        if (end > 0 && /^[A-Za-z.()<>^]+$/.test(inner) && (!isNotes || DYNAMICS.has(inner))) {
          v.pendingDecorations.push(inner);
          i = end + 1;
          continue;
        }
        if (end > 0) {
          // ABC 1.6 chords: +CEG+.
          i = this.chord(v, s, i + 1, end) ?? end + 1;
          continue;
        }
        i++;
        continue;
      }
      if (c === '.' && s[i + 1] === '(') {
        i++;
        continue;
      }
      if (c === '.' && s[i + 1] !== '|') {
        v.pendingDecorations.push('staccato');
        i++;
        continue;
      }
      if (c === '~') {
        v.pendingDecorations.push('roll');
        i++;
        continue;
      }
      if (/[H-Wh-w]/.test(c)) {
        const alias = this.decorationAliases.get(c);
        if (alias) v.pendingDecorations.push(alias);
        else if (SHORTCUTS.has(c)) v.pendingDecorations.push(c);
        i++;
        continue;
      }
      if (c === '(') {
        const t = /^\((\d+)(?::(\d*))?(?::(\d*))?/.exec(s.slice(i));
        if (t) {
          this.startTuplet(
            v,
            Number(t[1]),
            t[2] ? Number(t[2]) : undefined,
            t[3] ? Number(t[3]) : undefined,
          );
          i += t[0].length;
          continue;
        }
        v.pendingSlurs++;
        i++;
        continue;
      }
      if (c === ')') {
        const from = v.slurStack.pop();
        if (from && v.last && v.last.id !== from) {
          this.spanners.push({
            id: uid('abcs'),
            type: 'slur',
            fromEventId: from,
            toEventId: v.last.id,
          });
        }
        i++;
        continue;
      }
      if (c === '{') {
        const end = s.indexOf('}', i + 1);
        const inner = end < 0 ? s.slice(i + 1) : s.slice(i + 1, end);
        this.graces(v, inner);
        i = end < 0 ? s.length : end + 1;
        continue;
      }
      if (c === '[') {
        const inline = /^\[([A-Za-z]):([^\]]*)\]/.exec(s.slice(i));
        if (inline) {
          if (inline[1] === 'V') this.switchVoice(inline[2].trim());
          else if (inline[1] === 'w') {
            // lyrics inline are not aligned to anything sensible here
          } else this.inlineField(inline[1], inline[2].trim());
          i += inline[0].length;
          if (inline[1] === 'V') this.beginMusic(this.voice);
          continue;
        }
        const ending = /^\[(\d+(?:[-,.]\d+)*|"[^"]*")/.exec(s.slice(i));
        if (ending) {
          this.setEnding(v, ending[1]);
          i += ending[0].length;
          continue;
        }
        if (s[i + 1] === '|') {
          i = this.barline(v, s, i);
          continue;
        }
        const end = s.indexOf(']', i + 1);
        if (end < 0) {
          i++;
          continue;
        }
        i = this.chord(v, s, i + 1, end) ?? end + 1;
        continue;
      }
      if (c === '|' || c === ':' || (c === '.' && s[i + 1] === '|')) {
        const next = this.barline(v, s, i);
        i = next > i ? next : i + 1;
        continue;
      }
      if (c === '&') {
        this.overlay(v);
        i++;
        continue;
      }
      if (c === '>' || c === '<') {
        let n = 0;
        while (s[i + n] === c) n++;
        this.broken(v, c, n);
        i += n;
        continue;
      }
      if (c === '-') {
        this.tie(v);
        i++;
        continue;
      }
      if (/[_^=A-Ga-g]/.test(c)) {
        const note = this.readNote(v, s, i);
        if (!note) {
          i++;
          continue;
        }
        i = note.next;
        this.addEvent(v, {
          id: uid('abcn'),
          rest: false,
          heads: [{ pitch: note.pitch, written: note.written, tie: false }],
          written: mulF(v.unit, note.factor),
          at: ZERO,
        });
        if (s[i] === '-') {
          this.tie(v);
          i++;
        }
        continue;
      }
      if (c === 'z' || c === 'x') {
        const len = readLength(s, i + 1);
        i = len.next;
        this.addEvent(v, {
          id: uid('abcn'),
          rest: true,
          heads: [],
          written: mulF(v.unit, len.factor),
          at: ZERO,
        });
        continue;
      }
      if (c === 'Z' || c === 'X') {
        const m = /^\d+/.exec(s.slice(i + 1));
        const bars = m ? Number(m[0]) : 1;
        i += 1 + (m ? m[0].length : 0);
        this.multiRest(v, bars);
        continue;
      }
      if (c === 'y') {
        const len = readLength(s, i + 1);
        i = len.next;
        this.spacer(v);
        continue;
      }
      if (c === '\\') {
        i++;
        continue;
      }
      i++;
    }
  }

  /** A music line for this voice after w: lines opens a new lyric block. */
  private beginMusic(v: VoiceState): void {
    if (v.afterLyrics) {
      v.unalignedFrom = v.stream.length;
      v.verse = 0;
      v.afterLyrics = false;
    }
  }

  private quoted(v: VoiceState, raw: string): void {
    const first = raw[0];
    if (first === '^' || first === '_' || first === '<' || first === '>' || first === '@') {
      v.pendingAnnotations.push(raw);
      return;
    }
    // Several symbols on one note print stacked; the first is the chord.
    const parts = raw
      .split(/\n|;/)
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const reading = parseAbcChordSymbol(parts[0]);
    if (!reading) {
      // ABC 2.0 read any quoted text that was not a chord as an annotation.
      v.pendingAnnotations.push(`^${raw}`);
      return;
    }
    v.pendingChords.push(...parts);
  }

  private readNote(
    v: VoiceState,
    s: string,
    start: number,
  ): { pitch: SpelledPitch; written: Head['written']; factor: Fraction; next: number } | null {
    let i = start;
    let explicit: number | null = null;
    if (s[i] === '^' || s[i] === '_' || s[i] === '=') {
      const run = /^(\^\^|\^\/?|__|_\/?|=)/.exec(s.slice(i))!;
      if (run[0].endsWith('/')) this.warn('Quarter-tone accidentals are read as naturals');
      explicit =
        run[0] === '^^' ? 2 : run[0] === '^' ? 1 : run[0] === '__' ? -2 : run[0] === '_' ? -1 : 0;
      i += run[0].length;
    }
    const letter = s[i];
    if (!letter || !/[A-Ga-g]/.test(letter)) return null;
    i++;
    const step = letter.toUpperCase() as Step;
    let octave = letter === letter.toUpperCase() ? 4 : 5;
    while (s[i] === ',' || s[i] === "'") {
      octave += s[i] === "'" ? 1 : -1;
      i++;
    }
    const len = readLength(s, i);
    const alter = this.resolveAlter(v, step, octave, explicit);
    let pitch: SpelledPitch = { step, alter, octave: octave + v.octave };
    if (v.transpose) pitch = transposeBy(pitch, v.transpose);
    return { pitch, written: { step, octave, alter }, factor: len.factor, next: len.next };
  }

  /** Key signature, bar accidentals and ties decide the alteration. */
  private resolveAlter(v: VoiceState, step: Step, octave: number, explicit: number | null): number {
    const barKey = this.propagate === 'pitch' ? step : `${step}${octave}`;
    let alter: number;
    if (explicit !== null) {
      alter = explicit;
      if (this.propagate !== 'not') v.barAccidentals.set(barKey, explicit);
    } else if (v.tieCarry.has(`${step}${octave}`)) {
      alter = v.tieCarry.get(`${step}${octave}`)!;
    } else if (v.barAccidentals.has(barKey)) {
      alter = v.barAccidentals.get(barKey)!;
    } else {
      alter = v.key.accidentals.get(step) ?? 0;
    }
    return alter;
  }

  private chord(v: VoiceState, s: string, from: number, end: number): number | null {
    const inner = s.slice(from, end);
    const heads: Head[] = [];
    let factor: Fraction | null = null;
    let i = 0;
    while (i < inner.length) {
      const c = inner[i];
      if (c === '"') {
        const close = inner.indexOf('"', i + 1);
        i = close < 0 ? inner.length : close + 1;
        continue;
      }
      if (c === '!') {
        const close = inner.indexOf('!', i + 1);
        if (close > 0) v.pendingDecorations.push(inner.slice(i + 1, close));
        i = close < 0 ? inner.length : close + 1;
        continue;
      }
      if (/[_^=A-Ga-g]/.test(c)) {
        const note = this.readNote(v, inner, i);
        if (!note) {
          i++;
          continue;
        }
        i = note.next;
        let tie = false;
        if (inner[i] === '-') {
          tie = true;
          i++;
        }
        if (!factor) factor = note.factor;
        if (!heads.some((h) => midiOf(h.pitch) === midiOf(note.pitch))) {
          heads.push({ pitch: note.pitch, written: note.written, tie });
        }
        continue;
      }
      i++;
    }
    let next = end + 1;
    const outer = readLength(s, next);
    next = outer.next;
    if (heads.length === 0) return next;
    heads.sort((a, b) => midiOf(a.pitch) - midiOf(b.pitch));
    v.tieCarry.clear();
    this.addEvent(v, {
      id: uid('abcn'),
      rest: false,
      heads,
      written: mulF(mulF(v.unit, factor ?? frac(1)), outer.factor),
      at: ZERO,
    });
    for (const head of heads) if (head.tie) carryTie(v, head);
    if (s[next] === '-') {
      this.tie(v);
      next++;
    }
    return next;
  }

  private graces(v: VoiceState, inner: string): void {
    let i = inner.startsWith('/') ? 1 : 0;
    while (i < inner.length) {
      if (/[_^=A-Ga-g]/.test(inner[i])) {
        const note = this.readNote(v, inner, i);
        if (!note) {
          i++;
          continue;
        }
        i = note.next;
        const written = mulF(v.unit, note.factor);
        this.addEvent(v, {
          id: uid('abcn'),
          rest: false,
          heads: [{ pitch: note.pitch, written: note.written, tie: false }],
          written: compareF(written, frac(1, 2)) > 0 ? frac(1, 2) : written,
          grace: true,
          at: ZERO,
        });
        continue;
      }
      i++;
    }
  }

  private startTuplet(v: VoiceState, p: number, q?: number, r?: number): void {
    if (!(p >= 2 && p <= 9)) {
      this.warn(`Tuplet (${p} not understood`);
      return;
    }
    const compound = !!v.meter && v.meter[1] === 8 && v.meter[0] % 3 === 0 && v.meter[0] > 3;
    const normal =
      q ?? (p === 2 || p === 4 || p === 8 ? 3 : p === 3 || p === 6 ? 2 : compound ? 3 : 2);
    v.tuplet = { id: uid('abct'), p, q: normal, left: r ?? p };
  }

  private tie(v: VoiceState): void {
    const last = v.last;
    if (!last || last.rest) return;
    for (const head of last.heads) {
      head.tie = true;
      carryTie(v, head);
    }
  }

  private broken(v: VoiceState, symbol: '>' | '<', n: number): void {
    const last = v.last;
    const lane = v.bar.lanes[v.bar.lane];
    if (!last || lane.events[lane.events.length - 1] !== last || n > 3) return;
    const long = frac(2 ** (n + 1) - 1, 2 ** n);
    const short = frac(1, 2 ** n);
    const first = symbol === '>' ? long : short;
    const second = symbol === '>' ? short : long;
    const before = soundingOf(last);
    last.written = mulF(last.written, first);
    lane.pos = addF(lane.pos, sub(soundingOf(last), before));
    v.brokenNext = second;
  }

  private overlay(v: VoiceState): void {
    v.bar.lanes.push({ events: [], pos: ZERO });
    v.bar.lane = v.bar.lanes.length - 1;
    v.last = null;
    v.tuplet = null;
  }

  private spacer(v: VoiceState): void {
    const lane = v.bar.lanes[v.bar.lane];
    if (v.pendingChords.length) {
      v.bar.chords.push({ at: lane.pos, texts: v.pendingChords });
      v.pendingChords = [];
    }
  }

  private multiRest(v: VoiceState, bars: number): void {
    for (let k = 0; k < bars; k++) {
      if (k > 0) this.closeBar(v, { close: 'single', openRepeat: false });
      const capacity = v.bar.meter ? measureQuarters(v.bar.meter) : frac(4);
      this.addEvent(v, { id: uid('abcn'), rest: true, heads: [], written: capacity, at: ZERO });
    }
  }

  private setEnding(v: VoiceState, text: string): void {
    const n = /^\d+/.exec(text);
    if (/[-,]/.test(text)) this.warn('An ending for several passes is read as its first pass');
    const ending = n ? Number(n[0]) : 0;
    if (this.barIsEmpty(v.bar)) v.bar.ending = ending;
    else {
      this.closeBar(v, { close: 'single', openRepeat: false });
      v.bar.ending = ending;
    }
  }

  /** Read a barline at i; returns the index after it (and any ending). */
  private barline(v: VoiceState, s: string, start: number): number {
    let i = start;
    if (s[i] === '.' && s[i + 1] === '|') i++;
    const begin = i;
    while (i < s.length) {
      const c = s[i];
      if (c === ':' || c === '|') i++;
      else if (c === '[' && s[i + 1] === '|') i++;
      else if (c === ']' && s[i - 1] === '|') i++;
      else break;
    }
    const token = s.slice(begin, i);
    if (!token.includes('|') && !/^::+$/.test(token)) return start;
    const closeRepeat = token.startsWith(':');
    const openRepeat = token.endsWith(':');
    let close: BarlineClose = 'single';
    if (closeRepeat) close = 'repeat';
    else if (token.includes('|]')) close = token === '[|]' ? 'single' : 'final';
    else if (token.includes('||') || token.startsWith('[|')) close = 'double';
    this.closeBar(v, { close, openRepeat });
    const ending = /^\[?(\d+(?:[-,.]\d+)*)/.exec(s.slice(i));
    if (ending) {
      this.setEnding(v, ending[1]);
      i += ending[0].length;
    }
    return i;
  }

  private closeBar(v: VoiceState, spec: { close: BarlineClose; openRepeat: boolean }): void {
    this.navigationAtBarline(v);
    if (v.tuplet) {
      this.warn('A tuplet crossed a barline and was closed there');
      v.tuplet = null;
    }
    v.brokenNext = null;
    v.stream.push({ kind: 'bar' });
    const bar = v.bar;
    if (!this.barIsEmpty(bar)) {
      if (spec.close !== 'single') bar.close = spec.close;
      v.bars.push(bar);
      v.bar = this.newBar(v);
    } else if (spec.close !== 'single' && v.bars.length > 0) {
      const prev = v.bars[v.bars.length - 1];
      if (!prev.close || prev.close === 'single' || spec.close === 'repeat')
        prev.close = spec.close;
    }
    if (spec.openRepeat) v.bar.open = 'repeat';
    v.barAccidentals.clear();
  }

  /** Signs waiting at a barline: D.C./D.S./Fine end this bar; segno/coda mark the next. */
  private navigationAtBarline(v: VoiceState): void {
    // Between two barlines ("| !D.C.! |") the sign ends the bar before.
    const empty = this.barIsEmpty(v.bar);
    const ending = empty && v.bars.length > 0 ? v.bars[v.bars.length - 1] : v.bar;
    // ...and a segno or coda sign marks the bar after the barline.
    const starting = empty ? v.bar : v.next;
    const keep: string[] = [];
    for (const raw of v.pendingDecorations) {
      const name = decorationName(raw);
      const nav = navigationOf(name);
      if (!nav) {
        keep.push(raw);
        continue;
      }
      if (nav.kind === 'directive') ending.directive = nav.text;
      else if (nav.kind === 'segno') starting.segno = true;
      else starting.coda = true;
    }
    v.pendingDecorations = keep;
    const annotations: string[] = [];
    for (const raw of v.pendingAnnotations) {
      const text = decodeAbcText(raw.replace(/^[\^_<>@]/, '')).trim();
      if (TO_CODA.test(text)) starting.coda = true;
      else if (DIRECTIVE.test(text)) ending.directive = text;
      else annotations.push(raw);
    }
    v.pendingAnnotations = annotations;
  }

  // ----------------------------------------------------------- events

  private addEvent(v: VoiceState, ev: RawEvent): void {
    const bar = v.bar;
    const lane = bar.lanes[bar.lane];
    if (!ev.grace && !isPositive(ev.written)) return;
    if (!ev.grace) {
      if (v.brokenNext) {
        ev.written = mulF(ev.written, v.brokenNext);
        v.brokenNext = null;
      }
      if (v.tuplet) {
        ev.tuplet = { id: v.tuplet.id, p: v.tuplet.p, q: v.tuplet.q };
        v.tuplet.left--;
        if (v.tuplet.left <= 0) v.tuplet = null;
      }
      if (!ev.rest && ev.heads.length === 1) v.tieCarry.clear();
      else if (ev.rest) v.tieCarry.clear();
    }
    ev.at = lane.pos;
    if (!ev.grace) lane.pos = addF(lane.pos, soundingOf(ev));
    lane.events.push(ev);
    if (ev.grace) return;

    if (!ev.rest) v.hasNotes = true;
    const first = bar.lane === 0 && lane.events.filter((e) => !e.grace).length === 1;
    if (v.pendingChords.length) {
      bar.chords.push({ at: ev.at, texts: v.pendingChords });
      v.pendingChords = [];
    }
    for (const raw of v.pendingAnnotations) this.annotate(v, ev, raw, first);
    v.pendingAnnotations = [];
    for (const raw of v.pendingDecorations) this.decorate(v, ev, raw, first);
    v.pendingDecorations = [];
    for (; v.pendingSlurs > 0; v.pendingSlurs--) v.slurStack.push(ev.id);
    v.last = ev;
    if (bar.lane === 0) v.stream.push({ kind: ev.rest ? 'rest' : 'note', event: ev });
  }

  private endHairpin(v: VoiceState, to: string): void {
    const pin = v.hairpin;
    v.hairpin = null;
    if (pin && pin.from !== to) {
      this.spanners.push({ id: uid('abch'), type: pin.type, fromEventId: pin.from, toEventId: to });
    }
  }

  private annotate(v: VoiceState, ev: RawEvent, raw: string, first: boolean): void {
    const place = raw[0];
    const text = decodeAbcText(raw.slice(1)).trim();
    if (!text) return;
    const bar = v.bar;
    if (TO_CODA.test(text)) {
      // Play to here, then jump: on the bar's first note it marks this bar.
      if (first) bar.coda = true;
      else v.next.coda = true;
      return;
    }
    if (DIRECTIVE.test(text)) {
      bar.directive = text;
      return;
    }
    if (/^segno$/i.test(text)) {
      if (first) bar.segno = true;
      else v.next.segno = true;
      return;
    }
    if (first && SECTION_LABEL.test(text)) {
      bar.section = text;
      if (/^coda$/i.test(text)) bar.coda = true;
      return;
    }
    if (place === '_') ev.wordsBelow = ev.wordsBelow ? `${ev.wordsBelow} ${text}` : text;
    else ev.words = ev.words ? `${ev.words} ${text}` : text;
  }

  private decorate(v: VoiceState, ev: RawEvent, raw: string, first: boolean): void {
    const name = decorationName(raw);
    const nav = navigationOf(name);
    if (nav) {
      if (nav.kind === 'directive') v.bar.directive = nav.text;
      else if (nav.kind === 'segno') {
        if (first) v.bar.segno = true;
        else v.next.segno = true;
      } else if (first) v.bar.coda = true;
      else v.next.coda = true;
      return;
    }
    const add = <T>(list: T[] | undefined, item: T): T[] =>
      list?.includes(item) ? list : [...(list ?? []), item];
    switch (name) {
      case 'staccato':
        ev.articulations = add(ev.articulations, 'staccato');
        return;
      case 'accent':
      case '>':
      case 'emphasis':
      case 'L':
        ev.articulations = add(ev.articulations, 'accent');
        return;
      case 'tenuto':
        ev.articulations = add(ev.articulations, 'tenuto');
        return;
      case 'marcato':
      case '^':
        ev.articulations = add(ev.articulations, 'marcato');
        return;
      case 'trill':
      case 'trill(':
      case 'T':
        ev.ornaments = add(ev.ornaments, 'trill');
        return;
      case 'turn':
        ev.ornaments = add(ev.ornaments, 'turn');
        return;
      case 'invertedturn':
      case 'invertedturnx':
      case 'turnx':
        ev.ornaments = add(ev.ornaments, 'inverted-turn');
        return;
      case 'mordent':
      case 'lowermordent':
      case 'M':
        ev.ornaments = add(ev.ornaments, 'mordent');
        return;
      case 'uppermordent':
      case 'pralltriller':
      case 'P':
        ev.ornaments = add(ev.ornaments, 'inverted-mordent');
        return;
      case 'fermata':
      case 'invertedfermata':
      case 'H':
        v.bar.fermata = true;
        return;
      case 'pppp':
      case 'ppp':
      case 'pp':
        ev.dynamic = 'pp';
        return;
      case 'p':
      case 'mp':
      case 'mf':
      case 'f':
        ev.dynamic = name as DynamicMark;
        return;
      case 'ff':
      case 'fff':
      case 'ffff':
        ev.dynamic = 'ff';
        return;
      case 'crescendo(':
      case '<(':
        this.openHairpin(v, ev, 'cresc');
        return;
      case 'diminuendo(':
      case '>(':
        this.openHairpin(v, ev, 'dim');
        return;
      case 'crescendo)':
      case '<)':
      case 'diminuendo)':
      case '>)':
        if (v.hairpin) this.endHairpin(v, ev.id);
        return;
      default:
        if (!QUIET_DECORATIONS.test(name)) this.ignoredDecorations.add(name);
    }
  }

  private openHairpin(v: VoiceState, ev: RawEvent, type: 'cresc' | 'dim'): void {
    v.hairpin = { type, from: ev.id };
  }

  private finishVoice(v: VoiceState): void {
    this.navigationAtBarline(v);
    if (!this.barIsEmpty(v.bar)) v.bars.push(v.bar);
    if (this.ignoredDecorations.size) {
      this.warn(`Not shown: ${[...this.ignoredDecorations].map((d) => `!${d}!`).join(' ')}`);
      this.ignoredDecorations.clear();
    }
  }

  // ----------------------------------------------------------- output

  build(): Score {
    const voices = this.order.map((id) => this.voices.get(id)!).filter((v) => v.bars.length > 0);
    const barCount = Math.max(0, ...voices.map((v) => v.bars.length));
    const form: FormMeasure[] = [];
    const inForce: { chord: AbcChordSymbol | null } = { chord: null };
    let previousMeter: [number, number] | null = null;
    let firstMeter: [number, number] = [4, 4];

    if (voices.some((v) => v.bars.some((bar) => bar.meter === null))) {
      this.warn('No M: meter; bar lengths follow the music');
    }
    for (let b = 0; b < barCount; b++) {
      const bars = voices.map((v) => v.bars[b]).filter((bar): bar is Bar => !!bar);
      const lead = bars[0];
      let length = ZERO;
      for (const bar of bars) for (const lane of bar.lanes) length = maxF(length, lane.pos);
      const declared = lead.meter;
      let meter: [number, number];
      let pickupBeats: number | undefined;
      if (declared) {
        const capacity = measureQuarters(declared);
        meter = declared;
        if (!isPositive(length)) {
          // a bar of grace notes or spacers: a full bar
        } else if (compareF(length, capacity) < 0) {
          pickupBeats = fractionToNumber(mulF(length, frac(declared[1], 4)));
        } else if (compareF(length, capacity) > 0) {
          meter = meterOfLength(length, declared[1]);
          this.warn(`Bar ${b + 1} is longer than ${declared[0]}/${declared[1]}`);
        }
      } else {
        meter = isPositive(length) ? meterOfLength(length, 4) : (previousMeter ?? [4, 4]);
      }

      const fm: FormMeasure = { id: uid('abcm'), harmony: [] };
      if (
        b === 0 ||
        !previousMeter ||
        previousMeter[0] !== meter[0] ||
        previousMeter[1] !== meter[1]
      ) {
        fm.timeSig = [...meter];
      }
      if (b === 0) firstMeter = meter;
      previousMeter = meter;
      if (pickupBeats !== undefined) fm.pickupBeats = pickupBeats;
      const barQuarters =
        pickupBeats !== undefined
          ? mulF(frac(pickupBeats), frac(4, meter[1]))
          : measureQuarters(meter);

      const flag = <K extends keyof Bar>(key: K): Bar[K] | undefined =>
        bars.find((bar) => bar[key] !== undefined)?.[key];
      if (b === 0) fm.keySig = lead.keySig ?? this.defaults.key.signature;
      else if (lead.keySig) fm.keySig = lead.keySig;
      if (lead.open) fm.open = lead.open;
      if (lead.close) fm.close = lead.close;
      if (lead.ending !== undefined) fm.ending = lead.ending;
      const section = flag('section');
      if (section) fm.section = section;
      if (flag('segno')) fm.segno = true;
      if (flag('coda')) fm.coda = true;
      if (bars.some((bar) => bar.fermata)) fm.fermata = true;
      const directive = flag('directive');
      if (directive) fm.directive = directive;
      const staffText = flag('staffText');
      if (staffText) {
        fm.staffText = staffText;
        fm.staffTextAbove = true;
      }
      const tempoQ = flag('tempoQ');
      if (tempoQ && b > 0) fm.tempo = Math.round(tempoQ * (meter[1] / 4));

      const chordBar = bars.find((bar) => bar.chords.length > 0);
      fm.harmony = chordBar ? this.harmonyOf(chordBar, barQuarters, meter, inForce) : [];
      form.push(fm);
    }

    this.completeNavigation(form);

    const parts: Part[] = [];
    voices.forEach((v, index) => {
      if (!v.hasNotes) return;
      const role: PartRole = parts.length === 0 ? 'lead' : v.clef === 'bass' ? 'bass' : 'other';
      const partId = `part:abc:${index}`;
      const measures: PartMeasure[] = [];
      for (let b = 0; b < barCount; b++) {
        const bar = v.bars[b];
        const pmVoices: Voice[] = [];
        if (bar) {
          for (const lane of bar.lanes) {
            if (lane.events.length === 0) continue;
            pmVoices.push({ id: uid('abcv'), items: this.itemsOf(lane.events) });
          }
        }
        measures.push({ id: uid('abcpm'), voices: pmVoices });
      }
      completeTies(measures);
      const interval = v.transpose ? transposeInterval(v.transpose) : null;
      parts.push({
        id: partId,
        name: v.name ?? (voices.length > 1 ? `Voice ${v.id}` : 'Lead'),
        ...(role === 'lead' ? { instrument: 'lead' as const } : {}),
        role,
        // Written = concert + transposition; transpose= said how the part sounds.
        ...(interval
          ? { transposition: { degrees: -interval.degrees, semitones: -interval.semitones } }
          : {}),
        staves: [{ id: `${partId}:staff`, clef: v.clef, staffType: 'standard' }],
        measures,
      });
    });

    if (form.length === 0) {
      form.push({ id: uid('abcm'), harmony: [], timeSig: firstMeter });
    }
    this.checkPartOrder(form);

    const quarterBpm =
      this.tempoQ ??
      (this.tempoBare !== undefined ? this.tempoBare * (4 / firstMeter[1]) : undefined);
    const tempo = quarterBpm ? Math.round(quarterBpm * (firstMeter[1] / 4)) : undefined;
    const score: Score = {
      title: this.title || 'Untitled',
      ...(this.composers.length ? { composer: this.composers.join(', ') } : {}),
      ...(this.style ? { style: this.style } : {}),
      ...(tempo && tempo > 0 ? { tempo } : {}),
      ...(this.tempoText ? { tempoText: this.tempoText } : {}),
      form,
      parts,
      spanners: this.spanners,
    };
    return normalizeScore(score);
  }

  /** Chord symbols at their onsets; the chord in force restates at a bar start. */
  private harmonyOf(
    bar: Bar,
    barQuarters: Fraction,
    meter: [number, number],
    inForce: { chord: AbcChordSymbol | null },
  ): HarmonySymbol[] {
    const perBeat = frac(meter[1], 4);
    const symbols: { at: Fraction; chord: AbcChordSymbol | null; alternate?: AbcChordSymbol }[] =
      [];
    for (const entry of [...bar.chords].sort((a, b) => compareF(a.at, b.at))) {
      if (compareF(entry.at, barQuarters) >= 0) continue;
      const readings = entry.texts.map((text) => parseAbcChordSymbol(text)).filter((r) => !!r);
      const primary = readings.find((r) => r.kind === 'nc' || !r.parenthesized);
      const optional = readings.find((r) => r.kind === 'chord' && r.parenthesized);
      if (!primary) {
        // Only an optional chord here: it prints, it does not change the harmony.
        const previous = symbols[symbols.length - 1];
        if (optional?.kind === 'chord' && previous?.chord && equalsF(previous.at, entry.at)) {
          previous.alternate = optional.chord;
        }
        continue;
      }
      const chord = primary.kind === 'chord' ? primary.chord : null;
      const previous = symbols[symbols.length - 1];
      if (previous && equalsF(previous.at, entry.at)) continue;
      symbols.push({
        at: entry.at,
        chord,
        ...(optional?.kind === 'chord' ? { alternate: optional.chord } : {}),
      });
    }
    if (symbols.length === 0) return [];
    if (isPositive(symbols[0].at)) symbols.unshift({ at: ZERO, chord: inForce.chord });
    const out: HarmonySymbol[] = symbols.map((symbol, k) => {
      const end = k + 1 < symbols.length ? symbols[k + 1].at : barQuarters;
      const beats = fractionToNumber(mulF(sub(end, symbol.at), perBeat));
      if (!symbol.chord) {
        return {
          id: uid('c'),
          root: '',
          quality: '',
          chordType: null,
          beats,
          noChord: true,
          symbol: 'N.C.',
        };
      }
      const chord = chordFromType(symbol.chord.root, symbol.chord.chordType, beats, {
        quality: symbol.chord.quality,
        ...(symbol.chord.bass ? { bass: symbol.chord.bass } : {}),
      });
      // chordFromType floors a chord at one beat for hand entry; a written
      // half-beat anticipation keeps its span.
      chord.beats = beats;
      if (symbol.alternate) {
        chord.alternate = {
          root: symbol.alternate.root,
          quality: symbol.alternate.quality,
          chordType: symbol.alternate.chordType,
          ...(symbol.alternate.bass ? { bass: symbol.alternate.bass } : {}),
        };
      }
      return chord;
    });
    inForce.chord = symbols[symbols.length - 1].chord;
    return out;
  }

  /** A bare D.C./D.S. means "al Fine" in a tune with a Fine, "al Coda" in one with a coda. */
  private completeNavigation(form: FormMeasure[]): void {
    const hasFine = form.some((m) => /^\s*fine\b/i.test(m.directive ?? ''));
    const codas = form.filter((m) => m.coda).length;
    for (const m of form) {
      const bare = /^\s*(D\.\s*[CS]\.)\s*$/i.exec(m.directive ?? '');
      if (!bare) continue;
      if (hasFine) m.directive = `${bare[1]} al Fine`;
      else if (codas > 0) m.directive = `${bare[1]} al Coda`;
    }
  }

  private checkPartOrder(form: FormMeasure[]): void {
    if (!this.partOrder) return;
    const collapse = (letters: string[]) => letters.filter((l, k) => l !== letters[k - 1]).join('');
    const wanted = collapse(this.partOrder.replace(/[^A-Za-z]/g, '').split(''));
    const written = collapse(form.flatMap((m) => (m.section ? [m.section[0]] : [])));
    if (wanted && wanted !== written) {
      this.warn(`Part order ${this.partOrder} is not applied; parts play as written`);
    }
  }

  /** One lane's events as voice items: explicit tuplets grouped, long values tied. */
  private itemsOf(events: RawEvent[]): VoiceItem[] {
    this.implicitTriplets(events);
    // A grace note between tuplet members belongs to the group it leads into.
    const groupOf = new Map<RawEvent, RawEvent['tuplet']>();
    let following: RawEvent['tuplet'];
    for (let k = events.length - 1; k >= 0; k--) {
      const ev = events[k];
      if (ev.grace) groupOf.set(ev, following);
      else following = ev.tuplet;
      if (!ev.grace) groupOf.set(ev, ev.tuplet);
    }
    const out: VoiceItem[] = [];
    let group: TupletGroup | null = null;
    for (const ev of events) {
      const tuplet = groupOf.get(ev);
      const pieces = this.noteEventsOf(ev);
      if (tuplet) {
        if (!group || group.id !== tuplet.id) {
          group = { id: tuplet.id, kind: 'tuplet', actual: tuplet.p, normal: tuplet.q, items: [] };
          out.push(group);
        }
        group.items.push(...pieces);
      } else {
        group = null;
        out.push(...pieces);
      }
    }
    return out;
  }

  /** Lengths like A/3 outside a written tuplet read as triplets where they can. */
  private implicitTriplets(events: RawEvent[]): void {
    let id: string | null = null;
    for (const ev of events) {
      if (ev.grace) continue;
      if (ev.tuplet || isDyadic(ev.written)) {
        id = null;
        continue;
      }
      const scaled = mulF(ev.written, frac(3, 2));
      if (STRAIGHT.some((c) => equalsF(c.quarters, scaled))) {
        id = id ?? uid('abct');
        ev.written = scaled;
        ev.tuplet = { id, p: 3, q: 2 };
      } else {
        id = null;
        ev.written = frac(Math.max(1, Math.round(fractionToNumber(ev.written) * 32)), 32);
        this.warn('A note length no written value holds was rounded');
      }
    }
  }

  private noteEventsOf(ev: RawEvent): NoteEvent[] {
    if (ev.grace) {
      const fit =
        STRAIGHT.find((c) => compareF(c.quarters, ev.written) <= 0) ??
        STRAIGHT[STRAIGHT.length - 1];
      return [
        {
          id: ev.id,
          kind: 'note',
          duration: fit.duration,
          notes: ev.heads.map((h) => ({ pitch: h.pitch })),
          grace: true,
        },
      ];
    }
    const pieces = notate(ev.written);
    return pieces.map((duration, i) => {
      const event: NoteEvent = {
        id: i === 0 ? ev.id : `${ev.id}~${i}`,
        kind: ev.rest ? 'rest' : 'note',
        duration,
        notes: ev.rest
          ? []
          : ev.heads.map((h): NoteHead => {
              const stop = i > 0;
              const start = i < pieces.length - 1 || h.tie;
              const tie = stop && start ? 'both' : stop ? 'stop' : start ? 'start' : undefined;
              return { pitch: h.pitch, ...(tie ? { tie } : {}) };
            }),
      };
      if (i === 0) {
        if (ev.articulations) event.articulations = ev.articulations;
        if (ev.ornaments) event.ornaments = ev.ornaments;
        if (ev.dynamic) event.dynamic = ev.dynamic;
        if (ev.words) event.words = ev.words;
        if (ev.wordsBelow) event.wordsBelow = ev.wordsBelow;
        if (ev.lyrics && !ev.rest) event.lyrics = ev.lyrics;
      }
      return event;
    });
  }
}

// ---------------------------------------------------------------- helpers

/** The next note of the same written step and octave keeps a tied note's alteration. */
function carryTie(v: VoiceState, head: Head): void {
  v.tieCarry.set(`${head.written.step}${head.written.octave}`, head.written.alter);
}

/** The diatonic interval a transpose= of this many semitones moves by. */
function transposeInterval(semitones: number): { degrees: number; semitones: number } {
  const octaves = Math.trunc(semitones / 12);
  const rest = semitones - octaves * 12;
  const DEGREES = [0, 1, 1, 2, 2, 3, 3, 4, 5, 5, 6, 6];
  return { degrees: Math.sign(rest) * DEGREES[Math.abs(rest)] + 7 * octaves, semitones };
}

/** Written → concert for a transpose= voice (it sounds this many semitones from where it is written). */
function transposeBy(pitch: SpelledPitch, semitones: number): SpelledPitch {
  try {
    return transposePitch(pitch, transposeInterval(semitones));
  } catch {
    return pitch;
  }
}

function readLength(s: string, start: number): { factor: Fraction; next: number } {
  const m = /^(\d*)(\/*)(\d*)/.exec(s.slice(start))!;
  const [all, numText, slashes, denText] = m;
  if (!all) return { factor: frac(1), next: start };
  const num = numText ? Number(numText) : 1;
  let den = 1;
  if (slashes.length > 0) {
    den = denText ? Number(denText) * 2 ** (slashes.length - 1) : 2 ** slashes.length;
  } else if (denText) {
    // unreachable: digits without a slash were all read as the numerator
  }
  if (num <= 0 || den <= 0) return { factor: frac(1), next: start + all.length };
  return { factor: frac(num, den), next: start + all.length };
}

function soundingOf(ev: RawEvent): Fraction {
  if (ev.grace) return ZERO;
  return ev.tuplet ? mulF(ev.written, frac(ev.tuplet.q, ev.tuplet.p)) : ev.written;
}

function isDyadic(f: Fraction): boolean {
  let den = f.den;
  while (den % 2 === 0) den /= 2;
  return den === 1;
}

/** Largest-first written values for a length; tied when no single value holds it. */
function notate(length: Fraction): Duration[] {
  const out: Duration[] = [];
  let rest = length;
  while (isPositive(rest)) {
    const fit = STRAIGHT.find((c) => compareF(c.quarters, rest) <= 0);
    if (!fit) break;
    out.push(fit.duration);
    rest = sub(rest, fit.quarters);
  }
  return out.length ? out : [{ base: '128th' }];
}

/** A time signature that holds exactly this many quarters. */
function meterOfLength(length: Fraction, preferDen: number): [number, number] {
  for (const den of [preferDen, 4, 8, 16, 32]) {
    const beats = mulF(length, frac(den, 4));
    if (beats.den === 1 && beats.num >= 1) return [beats.num, den];
  }
  return [Math.max(1, Math.round(fractionToNumber(length))), 4];
}

/**
 * ABC writes only the outgoing tie; the score model needs both ends. A tie
 * meets the next sounding event's head of the same pitch (grace notes are
 * skipped); one that meets nothing — a rest, another pitch, the end — is
 * dropped. The main lane runs across barlines; an overlay (&) stays inside
 * its bar.
 */
function completeTies(measures: PartMeasure[]): void {
  const flat = (items: VoiceItem[]): NoteEvent[] =>
    items.flatMap((it) => (it.kind === 'tuplet' ? flat(it.items) : [it]));
  const starts = (h: NoteHead) => h.tie === 'start' || h.tie === 'both';
  const settle = (prev: NoteEvent | null, next: NoteEvent | null) => {
    if (!prev) return;
    const open = new Set(prev.notes.filter(starts).map((h) => midiOf(h.pitch)));
    const here = new Set((next?.notes ?? []).map((h) => midiOf(h.pitch)));
    for (const head of next?.notes ?? []) {
      if (open.has(midiOf(head.pitch))) head.tie = starts(head) ? 'both' : 'stop';
    }
    for (const head of prev.notes) {
      if (!starts(head) || here.has(midiOf(head.pitch))) continue;
      if (head.tie === 'both') head.tie = 'stop';
      else delete head.tie;
    }
  };
  let main: NoteEvent | null = null;
  for (const measure of measures) {
    measure.voices.forEach((voice, k) => {
      const events = flat(voice.items).filter((ev) => !ev.grace);
      if (k === 0) {
        for (const ev of events) {
          settle(main, ev);
          main = ev;
        }
        return;
      }
      let prev: NoteEvent | null = null;
      for (const ev of events) {
        settle(prev, ev);
        prev = ev;
      }
      settle(prev, null);
    });
  }
  settle(main, null);
}

const QUIET_DECORATIONS =
  /^(?:roll|breath|upbow|downbow|u|v|~|slide|wedge|open|thumb|snap|plus|\+|arpeggio|editorial|courtesy|invisible|shortphrase|mediumphrase|longphrase|[0-5]|trem\d|xstem|beambr\d|beamon|8va[()]?|8vb[()]?|15ma[()]?|ped|ped-up|sfz?|fp|rfz|trill\))$/i;

function decorationName(raw: string): string {
  const name = raw.trim();
  return name.length === 1 ? name : name.replace(/\s+/g, '');
}

type Navigation = { kind: 'directive'; text: string } | { kind: 'segno' } | { kind: 'coda' };

function navigationOf(name: string): Navigation | null {
  const n = name.toLowerCase();
  if (n === 'segno' || name === 'S') return { kind: 'segno' };
  if (n === 'coda' || name === 'O' || n === 'dacoda') return { kind: 'coda' };
  if (n === 'd.c.' || n === 'dacapo') return { kind: 'directive', text: 'D.C.' };
  if (n === 'd.s.') return { kind: 'directive', text: 'D.S.' };
  if (n === 'fine') return { kind: 'directive', text: 'Fine' };
  if (n === 'd.c.alfine') return { kind: 'directive', text: 'D.C. al Fine' };
  if (n === 'd.c.alcoda') return { kind: 'directive', text: 'D.C. al Coda' };
  if (n === 'd.s.alfine') return { kind: 'directive', text: 'D.S. al Fine' };
  if (n === 'd.s.alcoda') return { kind: 'directive', text: 'D.S. al Coda' };
  return null;
}

// ------------------------------------------------------------------- API

/** Parse the first tune of an ABC text (a whole file or one chunk). */
export function parseAbcTune(text: string): AbcImport {
  const book = splitAbcBook(text);
  const source = book.tunes[0];
  if (!source) throw new Error('No ABC tune found (a tune needs an X: or K: line).');
  return parseAbcChunk(source.chunk);
}

/** Parse one self-contained tune chunk (as `splitAbcBook` produces). */
export function parseAbcChunk(chunk: string): AbcImport {
  const parser = new TuneParser();
  parser.parse(chunk);
  const score = parser.build();
  return {
    title: score.title,
    ...(score.composer ? { composer: score.composer } : {}),
    score,
    warnings: [...parser.warnings],
  };
}
